import React, { useState, useCallback, useEffect } from 'react';
import Modal from 'react-modal';
import Cropper from 'react-easy-crop';
import { Button } from './Button';
import { ReactComponent as CloseIcon } from '../assets/icons/x.svg';
import { getCroppedImage, CroppedAreaPixels } from '../helpers/cropImage';
import { isCropLargeEnough } from '../helpers/popupCampaignForm';

interface ImageEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (croppedFile: File) => void;
  imageFile: File | null;
  aspectRatio: number;
  title?: string;
  /** Extra style for the crop box (react-easy-crop `style.cropAreaStyle`), e.g. a safe-zone gradient. */
  cropAreaStyle?: React.CSSProperties;
  /** Shown under the cropper, e.g. creative guidelines. */
  hint?: React.ReactNode;
  /** Confirm is disabled and an error shown when the cropped output is smaller than this. */
  minOutputSize?: { width: number; height: number };
}

export const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  imageFile,
  aspectRatio,
  title = 'Edit Image',
  cropAreaStyle,
  hint,
  minOutputSize,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] =
    useState<CroppedAreaPixels | null>(null);
  const [processing, setProcessing] = useState(false);

  const tooSmall =
    !!minOutputSize &&
    !!croppedAreaPixels &&
    !isCropLargeEnough(
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      minOutputSize.width,
      minOutputSize.height
    );

  // Load image when file changes
  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(imageFile);
    } else {
      setImageSrc(null);
    }
  }, [imageFile]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setImageSrc(null);
    }
  }, [isOpen]);

  const onCropComplete = useCallback(
    (_croppedArea: unknown, croppedAreaPixels: CroppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels || !imageFile) return;
    if (tooSmall) return;

    setProcessing(true);
    try {
      const croppedFile = await getCroppedImage(
        imageSrc,
        croppedAreaPixels,
        rotation,
        imageFile.name
      );
      onConfirm(croppedFile);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  const getAspectRatioLabel = () => {
    if (aspectRatio === 1) return '1:1';
    if (aspectRatio === 9 / 10) return '9:10';
    if (aspectRatio === 4 / 3) return '4:3';
    return `${aspectRatio.toFixed(2)}`;
  };

  return (
    <Modal
      className="modal-content md:max-w-[700px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={title}
    >
      {/* Header */}
      <div className="flex justify-between mb-4">
        <div>
          <h2 className="text-primary font-medium text-xl">{title}</h2>
          <p className="text-sm text-gray-500">
            Aspect ratio: {getAspectRatioLabel()}
          </p>
        </div>
        <Button onClick={onClose} variant="icon" disabled={processing}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      {/* Cropper Area */}
      {imageSrc && (
        <div className="relative w-full h-80 bg-gray-900 rounded-lg overflow-hidden mb-4">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            style={cropAreaStyle ? { cropAreaStyle } : undefined}
          />
        </div>
      )}
      {hint && <div className="mt-3 text-xs text-gray-500">{hint}</div>}
      {tooSmall && croppedAreaPixels && minOutputSize && (
        <div className="mt-2 text-xs text-red-600">
          Cropped area is {Math.round(croppedAreaPixels.width)} × {Math.round(croppedAreaPixels.height)} px.
          Minimum is {minOutputSize.width} × {minOutputSize.height} px — upload a larger image or zoom out.
        </div>
      )}

      {/* Controls */}
      <div className="space-y-4 mb-6">
        {/* Zoom Control */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium w-16">Zoom</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
          />
          <span className="text-sm w-12 text-right">{zoom.toFixed(1)}x</span>
        </div>

        {/* Rotation Control */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium w-16">Rotate</label>
          <input
            type="range"
            min={0}
            max={360}
            step={1}
            value={rotation}
            onChange={(e) => setRotation(Number(e.target.value))}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
          />
          <span className="text-sm w-12 text-right">{rotation}°</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={handleReset}
          disabled={processing}
        >
          Reset
        </Button>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            isLoading={processing}
            disabled={tooSmall}
          >
            Apply Crop
          </Button>
        </div>
      </div>
    </Modal>
  );
};
